import { useState, type ReactNode } from 'react';
import { classic42 } from '@risk/maps';
import type { TerritoryAssignment } from '@risk/engine';
import type { ClientState, GameClient } from '../net/client.js';
import { SeatDot } from '../components/common.js';
import { TerritoryList } from '../components/TerritoryList.js';

/**
 * Recording the physical setup.
 *
 * The app does not deal. The table lays the board out however it likes -- and
 * every table does it differently -- then tells the app the result. "Deal at
 * random" is only a shortcut for groups who wanted a random start anyway.
 */
export function Setup({ state, client }: { state: ClientState; client: GameClient }): ReactNode {
  const seats = state.game.seats;
  const [brush, setBrush] = useState(seats[0]?.id ?? '');

  const assigned = Object.keys(state.game.territories).length;
  const total = classic42.territories.length;

  const assign = (territory: string): void => {
    if (!brush) return;
    const current = state.game.territories[territory];
    client.act({
      type: 'assignTerritories',
      assignments: [{ territory, seat: brush, armies: current?.owner === brush ? current.armies : 1 }],
    });
  };

  const setArmies = (territory: string, armies: number): void => {
    const current = state.game.territories[territory];
    if (!current?.owner) return;
    client.act({
      type: 'assignTerritories',
      assignments: [{ territory, seat: current.owner, armies }],
    });
  };

  const dealAtRandom = (): void => {
    const ids = classic42.territories.map((t) => t.id);
    for (let i = ids.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    }
    const assignments: TerritoryAssignment[] = ids.map((territory, i) => ({
      territory,
      seat: seats[i % seats.length]!.id,
      armies: 1,
    }));
    client.act({ type: 'assignTerritories', assignments });
  };

  return (
    <div className="stack">
      <div className="card">
        <h2>Set up the board</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          Pick a player, then tap the territories they hold. Adjust the army
          counts to match what is actually on the table.
        </p>
        <div className="row wrap" style={{ marginBottom: 10 }}>
          {seats.map((seat) => (
            <button
              key={seat.id}
              className={brush === seat.id ? 'primary small' : 'small'}
              onClick={() => setBrush(seat.id)}
            >
              <SeatDot color={seat.color} /> {seat.name}
            </button>
          ))}
        </div>
        <div className="row">
          <button className="grow" onClick={dealAtRandom}>Deal at random</button>
        </div>
        <p className="small muted" style={{ marginBottom: 0 }}>
          {assigned} of {total} territories recorded
        </p>
      </div>

      <TerritoryList
        game={state.game}
        seatId={state.seatId}
        onSelect={assign}
        trailing={(id) => {
          const territory = state.game.territories[id];
          if (!territory?.owner) return null;
          return (
            <span className="row" style={{ gap: 4 }}>
              <button
                className="small"
                aria-label={`fewer armies in ${id}`}
                onClick={() => setArmies(id, Math.max(0, territory.armies - 1))}
              >−</button>
              <button
                className="small"
                aria-label={`more armies in ${id}`}
                onClick={() => setArmies(id, territory.armies + 1)}
              >+</button>
            </span>
          );
        }}
      />

      <div className="bar">
        <div className="inner">
          <span className="grow small muted">{assigned}/{total} recorded</span>
          <button className="primary" onClick={() => client.act({ type: 'beginPlay' })}>
            Begin play
          </button>
        </div>
      </div>
    </div>
  );
}
